import { capitalizeExportValue } from 'lib/helpers/capitalizeExportValue';
import {
  customEval,
  isArrayWithPrimitive,
  isPrimitiveValue,
  mString,
  removeHtmlTags
} from 'lib/helpers/utility';

// Fields a renderer-only column most often carries its human label in. An exporter can't
// run JSX, so without this an object/array cell exports as '-' even though the grid shows
// text — see `labelFromObject` usage below.
// `id` is last: a row of ids is what columns like "Claim ID" actually display, but any
// human-readable key is preferred over it.
const LABEL_KEYS = [
  'label',
  'name',
  'title',
  'display_name',
  'code',
  'state',
  'status',
  'value',
  'id'
];

const labelFromObject = (item) => {
  if (item === null || item === undefined) return null;
  if (isPrimitiveValue(item)) return String(item);
  if (typeof item !== 'object') return null;

  const fullName = [item.f_name, item.l_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;

  const key = LABEL_KEYS.find(
    (candidate) => item[candidate] !== undefined && isPrimitiveValue(item[candidate])
  );

  return key ? String(item[key]) : null;
};

// Best-effort text for a cell whose value is an object or a list of them. Returns null when
// nothing readable is found, so the caller can fall back to its own '-'.
export const labelFromValue = (value) => {
  if (Array.isArray(value)) {
    const labels = value.map(labelFromObject).filter(Boolean);
    return labels.length ? labels.join(', ') : null;
  }

  return labelFromObject(value);
};

export const getNestedValue = (nestedValue) => {
  // if its object with value and href, example in reports
  if (nestedValue !== undefined) {
    // typeof null is 'object' — a null id (e.g. an "Unknown Patient" row) has nothing to entry.
    if (nestedValue === null || typeof nestedValue !== 'object') {
      return nestedValue;
    }

    const formatted = Object.entries(nestedValue)
      .map(([key, val]) => {
        if (typeof val === 'number' && !key.endsWith('_') && !key.includes('_id')) {
          return mString(val);
        }

        return val;
      })
      .join(' / ');

    return formatted;
  }
};

export default function extractDataFromColDefs(data, colDefs) {
  return data.map((row) => {
    const result = {};

    colDefs.forEach(({ field, valueFormatter, valueGetter }) => {
      if (!field) return;

      let rawValue = customEval(row, field);

      if (typeof rawValue === 'string') {
        rawValue = removeHtmlTags(rawValue);
      }

      if (valueGetter) {
        rawValue = valueGetter({ value: rawValue, data: row });

        // if there is no value formatter or cell renderer, we can just set the raw value and return
        if (!valueFormatter) {
          result[field] = rawValue;
          return;
        }
      }

      if (valueFormatter) {
        const formattedValue = valueFormatter({ value: rawValue, data: row });

        if (formattedValue) {
          result[field] = formattedValue;
          return;
        }
      }

      if (isPrimitiveValue(rawValue)) {
        result[field] = rawValue;
        return;
      }

      if (isArrayWithPrimitive(rawValue)) {
        result[field] = rawValue.join(', ');
        return;
      }

      const nestedValue = getNestedValue(rawValue?.value);

      if (nestedValue) {
        result[field] = nestedValue;
        return;
      }

      // A renderer-only column often holds objects (a payer, a list of forms, a claim). The
      // grid renders them through JSX an exporter can't run, so read the readable label off
      // the data rather than writing an empty cell.
      const label = labelFromValue(rawValue);

      result[field] = label ?? '-';
    });

    // Exported cells start with an uppercase letter; only the leading character changes.
    return Object.fromEntries(
      Object.entries(result).map(([field, value]) => [field, capitalizeExportValue(value)])
    );
  });
}
